import {translate} from "react-i18nify";

import * as rcc from "../../constants/rcc";
import * as settings from "../../constants/settings";
import * as config from "../../constants/config";
import * as formatHelper from "../../helpers/formatHelper";

/**
 * @class Colorant
 */
export default class Colorant {
    /**
     * @param {Number} id
     * @param {String} name
     * @param {String} displayName
     * @param {String} rgb
     * @param {Number} amount
     * @param {String} amountType
     * @param {Number} density
     * @param {String} ufi
     * @param {Number} validationResult
     * @param {String} warningText
     * @param {Number} dispensingLogic
     * @param {Number} correction
     * @param {Number} realAmount
     */
    constructor(
        id,
        name,
        displayName,
        rgb,
        amount,
        amountType = settings.UNIT_ML,
        density = 1,
        ufi = null,
        validationResult = rcc.RCC_VALIDATION_OK,
        warningText = "",
        dispensingLogic = rcc.DISPENSING_LOGIC_AUTOMATIC,
        correction = 0,
        realAmount = 0
    ) {
        this._id = id;
        this._name = name;
        this._displayName = displayName;
        this._rgb = rgb;
        this._amount = amount;
        this._realAmount = realAmount === 0 ? amount : realAmount;
        this._amountType = amountType;
        this._density = density;
        this._ufi = ufi;
        this._validationResult = validationResult;
        this._warningText = warningText;
        this._dispensingLogic = dispensingLogic;
        this._correction = correction;

        if (typeof this._id !== "number") {
            throw new Error("Invalid datatype of ID - Colorant");
        }
    }

    /**
     * @return {Number}
     */
    get id() {
        return this._id;
    }

    /**
     * @param {Number} id
     */
    set id(id) {
        this._id = id;
    }

    /**
     * @return {String}
     */
    get name() {
        return this._name;
    }

    /**
     * @param {String} name
     */
    set name(name) {
        this._name = name;
    }

    /**
     * @return {String}
     */
    get displayName() {
        return this._displayName;
    }

    /**
     * @param {String} displayName
     */
    set displayName(displayName) {
        this._displayName = displayName;
    }

    /**
     * @return {String}
     */
    get rgb() {
        return this._rgb;
    }

    /**
     * @param {String} rgb
     */
    set rgb(rgb) {
        this._rgb = rgb;
    }

    /**
     * @return {Number}
     */
    get amount() {
        return this._amount;
    }

    /**
     * Colorant rounded amount or real updated value without rounding.
     *
     * @param {Number} amount
     */
    set amount(amount) {
        this._amount = amount;
    }

    /**
     * Colorant real amount with no rounding.
     *
     * @return {Number}
     */
    get realAmount() {
        return this._realAmount;
    }

    /**
     * @param {Number} realAmount
     */
    set realAmount(realAmount) {
        this._realAmount = realAmount;
    }

    /**
     * @return {String}
     */
    get amountType() {
        return this._amountType;
    }

    /**
     * @param {String} amountType
     */
    set amountType(amountType) {
        this._amountType = amountType;
    }

    /**
     * @return {Number}
     */
    get density() {
        return this._density;
    }

    /**
     * @param {Number} density
     */
    set density(density) {
        this._density = density;
    }

    /**
     * @return {String}
     */
    get ufi() {
        return this._ufi;
    }

    /**
     * @param {String} ufi
     */
    set ufi(ufi) {
        this._ufi = ufi;
    }

    /**
     * @return {Number}
     */
    get validationResult() {
        return this._validationResult;
    }

    /**
     * @param {Number} validationResult
     */
    set validationResult(validationResult) {
        this._validationResult = validationResult;
    }

    /**
     * @return {String}
     */
    get warningText() {
        return this._warningText;
    }

    /**
     * @param {String} warningText
     */
    set warningText(warningText) {
        this._warningText = warningText;
    }

    /**
     * @return {Number}
     */
    get dispensingLogic() {
        return this._dispensingLogic;
    }

    /**
     * @param {Number} dispensingLogic
     */
    set dispensingLogic(dispensingLogic) {
        this._dispensingLogic = dispensingLogic;
    }

    /**
     * @return {Number}
     */
    get correction() {
        return this._correction;
    }

    /**
     * @param {Number} correction
     */
    set correction(correction) {
        this._correction = correction;
    }

    /**
     * @return {Array}
     */
    get prices() {
        return [];
    }

    /**
     * @return {Object}
     */
    get margin() {
        return null;
    }

    /**
     * @return {String}
     */
    toString() {
        return "Colorant (" + this._id + ")";
    }

    /**
     * @return {Boolean}
     */
    isEmpty() {
        return this._amount === 0;
    }

    /**
     * @return {Boolean}
     */
    isAdded() {
        return this._amount === this._correction;
    }

    /**
     * @return {Boolean}
     */
    isCorrectionEmpty() {
        return this._correction === 0;
    }

    /**
     * @return {String}
     */
    formatAmount() {
        return formatHelper.formatDecimal(this._amount);
    }

    /**
     * @return {String}
     */
    formatAmountType() {
        return this._amountType;
    }

    /**
     * @return {String}
     */
    getFormattedCorrection() {
        if (this._correction > 0) {
            return "+" + formatHelper.formatDecimal(this._correction);
        }

        return formatHelper.formatDecimal(this._correction);
    }

    /**
     * @return {String}
     */
    formatCorrection() {
        if (this._correction === 0) {
            return "";
        }

        return this.getFormattedCorrection();
    }

    /**
     * @return {String}
     */
    formatCorrectionForPrintData() {
        if (this._correction === 0) {
            return "0";
        }

        return this.getFormattedCorrection();
    }

    /**
     * @return {Colorant}
     */
    clone = () => {
        return new Colorant(
            this._id,
            this._name,
            this._displayName,
            this._rgb,
            this._amount,
            this._amountType,
            this._density,
            this._ufi,
            this._validationResult,
            this._warningText,
            this._dispensingLogic,
            this._correction,
            this._realAmount
        );
    };

    /**
     * Clones colorant object and updates validation result property.
     *
     * @param   {Number} result
     * @return  {Object}
     */
    updateValidationResult = (result) => {
        let warningText = "";
        switch (result) {
            case rcc.RCC_VALIDATION_WARNING_LEVEL:
                warningText = translate("general.warningLevel");
                break;
            case rcc.RCC_VALIDATION_CRITICAL_LEVEL:
                warningText = translate("general.criticalLevel");
                break;
            case rcc.RCC_VALIDATION_SPLIT_FORMULA:
                warningText = translate("mainTab.splitFormula");
                break;
            case rcc.RCC_VALIDATION_MANUAL_COLORANT_ADDITION:
            case rcc.RCC_VALIDATION_MANUAL_COLORANT_ADDITION_CUSTOM_CAN:
                warningText = translate("mainTab.manualColorantAddition");
                break;
            case rcc.RCC_VALIDATION_OVER_AVAILABLE_LEVEL:
                warningText = translate("mainTab.overAvailableLevel");
                break;
            case rcc.RCC_VALIDATION_MISSING_ITEM:
                warningText = translate("general.missingItem");
                break;
            case rcc.RCC_VALIDATION_OTHER_ERROR:
                warningText = translate("general.otherError");
                break;
            default:
                warningText = "";
                break;
        }

        let newColorant = this.clone();
        newColorant.validationResult = result;
        newColorant.warningText = warningText;

        return newColorant;
    };

    /**
     * Clones colorant object and updates amount property.
     *
     * @param   {Number} amount
     * @return  {Object}
     */
    updateAmount = (amount) => {
        if (amount > config.MAX_COLORANT_AMOUNT) {
            return this;
        }

        let newColorant = this.clone();
        newColorant.amount = amount;
        newColorant.realAmount = amount;

        return newColorant;
    };

    /**
     * Clones colorant object and updates correction property.
     *
     * @param   {Number} correction
     * @return  {Object}
     */
    updateCorrection = (correction) => {
        let newColorant = this.clone();
        newColorant.correction = correction;

        return newColorant;
    };

    /**
     * Clones colorant object and updates amount, real amount (amount with no rounding) and amountType properties.
     *
     * @param   {Number} amount
     * @param   {String} amountType
     * @return  {Object}
     */
    updateAmountValues = (amount, amountType) => {
        let newColorant = this.clone();
        newColorant.amount = amount;
        newColorant.realAmount = amount;
        newColorant.amountType = amountType;

        return newColorant;
    };

    /**
     * Clones colorant object and updates dispensingLogic property.
     *
     * @param   {Number} dispensingLogic
     * @return  {Object}
     */
    updateDispensingLogic = (dispensingLogic) => {
        let newColorant = this.clone();
        newColorant.dispensingLogic = dispensingLogic;

        return newColorant;
    };

    /**
     * Clones colorant object and updates amount and correction property.
     *
     * @return  {Object}
     */
    setEmpty = () => {
        let newColorant = this.clone();
        newColorant.correction = -newColorant.amount;
        newColorant.amount = 0;
        newColorant.realAmount = 0;

        return newColorant;
    };

    /**
     * Clones colorant object and updates rgb and density property.
     *
     * @param   {String} rgb
     * @param   {Number} density
     * @return  {Object}
     */
    updateCanisterValues = (rgb, density) => {
        let newColorant = this.clone();
        newColorant.density = density;

        if (rgb !== null) {
            newColorant.rgb = rgb;
        }

        return newColorant;
    };
}
